<template>
  <div class="message-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>站内消息</span>
          <el-button type="primary" size="small" @click="refreshMessages">刷新</el-button>
        </div>
      </template>

      <el-tabs v-model="activeTab" @tab-change="handleTabChange">
        <el-tab-pane label="未读消息" name="unread"></el-tab-pane>
        <el-tab-pane label="已读消息" name="read"></el-tab-pane>
        <el-tab-pane label="全部消息" name="all"></el-tab-pane>
        <div v-if="activeTab === 'unread'" style="margin-bottom: 10px; text-align: right;">
          <el-button type="primary" size="small" @click="handleBatchMarkAsRead">
            全已读
          </el-button>
        </div>
      </el-tabs>

      <el-table v-loading="loading" :data="messagesData" style="width: 100%">
        <el-table-column prop="priority" label="优先级" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.priority === MessagePriority.NORMAL.value ? 'info' : scope.row.priority === MessagePriority.HIGH.value ? 'warning' : scope.row.priority === MessagePriority.URGENT.value ? 'danger' : 'danger'">
              {{ MessagePriority[scope.row.priority].label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.isRead ? 'success' : 'warning'">
              {{ scope.row.isRead ? '已读' : '未读' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" width="200">
          <template #default="scope">
            <span :style="{ color: scope.row.title === '订单状态变更' ? 'blue' : 'inherit' }">
              {{ scope.row.title }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="content" label="内容">
          <template #default="scope">
            <div class="message-content" :class="{ 'unread': !scope.row.isRead }">
              {{ scope.row.content }}
            </div>
          </template>
        </el-table-column>
        <!-- <el-table-column prop="senderId" label="发送者ID" width="100"></el-table-column> -->
        <el-table-column prop="createTime" label="发送时间" width="180"></el-table-column>
 
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="scope">
            <el-button
              type="primary"
              size="small"
              @click="handleRead(scope.row.id)"
              :disabled="scope.row.isRead"
            >
              标记已读
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useStore } from 'vuex'
import { MessagePriority } from '@/store'

const store = useStore()
const loading = ref(false)
const messagesData = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(30)
const activeTab = ref('unread')
// const user = computed(() => store.state.user)
const receiverId = computed(() => store.state.user ? store.state.user.id : null)

// 获取消息列表
    const getMessages = async () => {
      loading.value = true
      try {
        const pagination = {
          pageNumber: currentPage.value - 1,
          pageSize: pageSize.value
        }

        // 根据当前标签页设置是否已读参数
        let isRead = null
        if (activeTab.value === 'unread') {
          isRead = false
        } else if (activeTab.value === 'read') {
          isRead = true
        }

        const payload = {
          pagination,
          isRead
        }

        store.dispatch('getMessages', payload).then(res => {
          messagesData.value = res
          // 从store中获取总条数
          total.value = store.state.messagePagination.total
        })
      } catch (error) {
        ElMessage.error('获取消息失败: ' + (error.response?.data?.message || error.message))
      } finally {
        loading.value = false
      }
    }

// 刷新消息列表
const refreshMessages = () => {
  getMessages()
}

// 处理标签页切换
const handleTabChange = () => {
  currentPage.value = 1
  getMessages()
}

// 处理分页大小变化
const handleSizeChange = (size) => {
  pageSize.value = size
  getMessages()
}

// 处理当前页码变化
const handleCurrentChange = (current) => {
  currentPage.value = current
  getMessages()
}

// 标记消息为已读
const handleRead = async (id) => {
    store.dispatch('markAsRead', id).then(res => {
      ElMessage.success('标记已读成功')
    // 重新获取消息列表
      getMessages()
    })
    .catch (error =>{
    ElMessage.error('标记已读失败: ' + (error.response?.data?.message || error.message))
  })
}

// 批量标记已读
const handleBatchMarkAsRead = () => {
  // 筛选出未读消息的id
  const unreadIds = messagesData.value
    .filter(message => !message.isRead)
    .map(message => message.id)

  if (unreadIds.length === 0) {
    ElMessage.info('没有未读消息需要标记')
    return
  }

  ElMessageBox.confirm('确定将所有未读消息标记为已读吗？', '确认操作', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'info'
  }).then(() => {
    store.dispatch('batchMarkAsRead', unreadIds)
      .then(() => {
        ElMessage.success('批量标记已读成功')
        // 刷新消息列表
        getMessages()
      })
      .catch(error => {
        ElMessage.error('批量标记已读失败: ' + (error.response?.data?.message || error.message))
      })
  })
}

// 删除消息
const handleDelete = async (id) => {
  ElMessageBox.confirm('确定删除该消息吗？', '删除确认', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    store.dispatch('deleteMessage', id)
    .then(() => {
      ElMessage.success('删除消息成功')
      getMessages()
    }).catch (error => {
      ElMessage.error('删除消息失败: ' + (error.response?.data?.message || error.message))
    })
  })
}

    // 获取未读消息数
    const fetchUnreadCount = async () => {
        store.dispatch('getUnreadCountMessage')
          .then(res => {
            console.log('unreadCount', res)
          })
          .catch(error => {
            console.error('获取未读消息数失败:', error)
            ElMessage.error('获取未读消息数失败: ' + (error.message || '未知错误'))
          })
        // }
      }

// SSE连接对象
const sseRef = ref(null)

  // 建立SSE连接
const establishSseConnection = () => {
  if (!receiverId.value) {
    store.dispatch("refreshToken")
    console.log("receiverId:"+receiverId);
  }
  // 关闭已存在的连接
  if (sseRef.value) {
    sseRef.value.close()
  }

  // 创建新的SSE连接
  store.dispatch('subscribeToMessages', receiverId.value).then(eventSource => {
    sseRef.value = eventSource

    // 监听连接打开事件
    sseRef.value.onmessage = () => {
      console.log('SSE收到消息')
      fetchUnreadCount()
    }

    sseRef.value.onopen = () => {
      console.log('SSE连接已建立')
      fetchUnreadCount()
    }

    // 监听新消息事件
    // sseRef.value.addEventListener('newMessage', (event) => {
    //   try {
    //     const newMessage = JSON.parse(event.data)
    //     ElMessage({
    //       message: `收到新消息: ${newMessage.title}`,
    //       type: 'info',
    //       duration: 3000
    //     })
    //     // 刷新消息列表
    //     fetchUnreadCount()
    //   } catch (error) {
    //     console.error('解析SSE消息失败:', error)
    //   }
    // })

    // 监听连接错误事件
    sseRef.value.onerror = (error) => {
      console.error('SSE连接错误:', error)
      // 尝试重新连接
      setTimeout(establishSseConnection, 5000)
    }
  }).catch(error => {
    console.error('创建SSE连接失败:', error)
    ElMessage.error('创建SSE连接失败: ' + (error.message || '未知错误'))
  })
}


// 页面加载时获取消息并建立SSE连接
onMounted(() => {
  getMessages()
  establishSseConnection()
})

// 页面卸载时关闭SSE连接
onUnmounted(() => {
  if (sseRef.value) {
    sseRef.value.close()
    sseRef.value = null
  }
})
</script>

<style scoped>
.message-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.message-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
}

.unread {
  font-weight: bold;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>